import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { login, ASSETS } from "../BaseURL";
import { useFacility, splitFacilityName } from "../context/FacilityContext";

export default function LoginPage() {
  const [form, setForm] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // If the operator was bounced here by the inactivity timer or a 401
  // from the API, surface the reason so they know why they're back at
  // the login screen.
  useEffect(() => {
    try {
      const reason = localStorage.getItem("session_expired");
      if (reason === "idle") {
        setNotice("You were signed out after 10 minutes of inactivity. Please sign in again.");
      } else if (reason === "auth") {
        setNotice("Your session has expired. Please sign in again.");
      }
      if (reason) localStorage.removeItem("session_expired");
    } catch (_) {}
  }, []);

  // Facility is preloaded by FacilityProvider; the RequireSetup guard already
  // sends users without a configured facility to /setup before they reach
  // this component.
  const { facility } = useFacility();
  const [firstWord, restName] = splitFacilityName(facility?.name);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(login, form);
      if (!res.data?.success) {
        setError(res.data?.message || "Login failed");
        return;
      }
      // No JWT — we mirror the TotalCare pattern of caching the user object.
      localStorage.setItem("user", JSON.stringify(res.data.user));
      // Users with a temporary password issued by a super_admin must rotate
      // it before they reach the dashboard.
      navigate(res.data.user?.first_login ? "/first-login" : "/management");
    } catch (err) {
      setError(err.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Jost:wght@300;400;500&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .login-root {
          min-height: 100vh;
          display: flex;
          font-family: 'Jost', sans-serif;
          background: #f5ede2;
        }

        /* ── LEFT PANEL ── */
        .login-left {
          flex: 1;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          padding: 52px 48px;
          overflow: hidden;
          background-color: #2c1f14;
          animation: panelIn 0.9s cubic-bezier(.22,1,.36,1) both;
        }

        /* Dimmed hotel photo, sitting beneath all decorative layers.
           A subtle Ken-Burns-style zoom keeps it from feeling static. */
        .left-photo {
          position: absolute;
          inset: 0;
          background-image: url('https://images.unsplash.com/photo-1564501049412-61c2a3083791?auto=format&fit=crop&w=1600&q=80');
          background-size: cover;
          background-position: center;
          filter: brightness(0.55) saturate(0.9) sepia(0.18);
          opacity: 0.42;
          transform: scale(1.05);
          animation: photoDrift 24s ease-in-out infinite alternate;
          z-index: 0;
        }

        /* Warm tint + vignette layered on top of the photo so the brand
           colour palette stays unmistakable and the design details pop. */
        .login-left::before {
          content: '';
          position: absolute;
          inset: 0;
          z-index: 1;
          background:
            radial-gradient(ellipse 80% 60% at 30% 20%, rgba(180,130,70,.18) 0%, transparent 70%),
            radial-gradient(ellipse 60% 80% at 80% 90%, rgba(90,50,20,.45) 0%, transparent 60%),
            linear-gradient(115deg, rgba(28,16,8,.55) 0%, rgba(20,10,4,.30) 55%, rgba(15,8,3,.75) 100%);
        }

        .left-texture {
          position: absolute;
          inset: 0;
          z-index: 2;
          background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          pointer-events: none;
        }

        .left-ornament {
          position: absolute;
          top: 40px;
          left: 48px;
          width: 48px;
          height: 48px;
          border: 1.5px solid rgba(200,160,90,.5);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(28,16,8,.42);
          backdrop-filter: blur(2px);
          -webkit-backdrop-filter: blur(2px);
          z-index: 3;
          animation: fadeUp 0.8s 0.3s both;
        }

        .left-ornament svg { opacity: 0.7; }

        .left-divider {
          width: 40px;
          height: 1.5px;
          background: linear-gradient(90deg, rgba(200,160,90,.8), transparent);
          margin-bottom: 18px;
          animation: fadeUp 0.7s 0.6s both;
        }

        .left-tagline {
          font-family: 'Cormorant Garamond', serif;
          font-style: italic;
          font-weight: 300;
          font-size: 15px;
          color: rgba(220,185,130,.75);
          letter-spacing: 0.06em;
          margin-bottom: 14px;
          animation: fadeUp 0.7s 0.65s both;
        }

        .left-title {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 300;
          font-size: clamp(36px, 4vw, 52px);
          line-height: 1.12;
          color: #f0e4cf;
          letter-spacing: -0.01em;
          margin-bottom: 20px;
          animation: fadeUp 0.8s 0.7s both;
        }

        .left-title em {
          font-style: italic;
          color: #c8a05a;
        }

        .left-desc {
          font-size: 13.5px;
          font-weight: 300;
          color: rgba(220,200,175,.55);
          line-height: 1.7;
          max-width: 280px;
          letter-spacing: 0.02em;
          animation: fadeUp 0.8s 0.8s both;
        }

        .left-footer {
          position: absolute;
          bottom: 32px;
          right: 48px;
          display: flex;
          gap: 8px;
          z-index: 3;
        }

        .dot { width: 5px; height: 5px; border-radius: 50%; background: rgba(200,160,90,.35); animation: fadeUp 0.5s 1s both; }
        .dot.active { background: rgba(200,160,90,.8); width: 18px; border-radius: 3px; }

        /* ── RIGHT PANEL ── */
        .login-right {
          width: min(480px, 100%);
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 60px 52px;
          background: #f5ede2;
          position: relative;
          animation: slideIn 0.85s cubic-bezier(.22,1,.36,1) both;
        }

        .login-right::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 1px;
          height: 100%;
          background: linear-gradient(to bottom, transparent, rgba(180,140,80,.2) 30%, rgba(180,140,80,.2) 70%, transparent);
        }

        .form-eyebrow {
          font-size: 10.5px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #c8a05a;
          margin-bottom: 12px;
          animation: fadeUp 0.6s 0.4s both;
        }

        .form-title {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 600;
          font-size: 34px;
          color: #2c1f14;
          line-height: 1.15;
          margin-bottom: 8px;
          animation: fadeUp 0.6s 0.5s both;
        }

        .form-subtitle {
          font-size: 13.5px;
          font-weight: 300;
          color: #8a7060;
          margin-bottom: 36px;
          letter-spacing: 0.02em;
          animation: fadeUp 0.6s 0.55s both;
        }

        .field-group {
          margin-bottom: 20px;
          animation: fadeUp 0.6s 0.6s both;
        }

        .field-group + .field-group { animation-delay: 0.65s; }

        .field-label {
          display: block;
          font-size: 10.5px;
          font-weight: 500;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: #6b5a4e;
          margin-bottom: 8px;
        }

        .field-wrap {
          position: relative;
        }

        .field-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #b8a090;
          width: 16px;
          height: 16px;
          pointer-events: none;
        }

        .field-input {
          width: 100%;
          padding: 14px 14px 14px 42px;
          border: 1.5px solid #e0d0c0;
          border-radius: 8px;
          background: #fdf8f2;
          font-family: 'Jost', sans-serif;
          font-size: 14px;
          font-weight: 400;
          color: #2c1f14;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          letter-spacing: 0.02em;
        }

        .field-input::placeholder { color: #c0aea0; font-weight: 300; }

        .field-input:focus {
          border-color: #c8a05a;
          box-shadow: 0 0 0 3px rgba(200,160,90,.1);
          background: #fff;
        }

        .password-toggle {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: #b8a090;
          padding: 2px;
          display: flex;
          align-items: center;
          transition: color 0.2s;
        }
        .password-toggle:hover { color: #8a7060; }

        .error-box {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: #fff0ee;
          border: 1px solid #f5c5bc;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 13px;
          color: #c0392b;
          animation: shake 0.4s cubic-bezier(.36,.07,.19,.97) both;
        }

        .login-btn {
          width: 100%;
          padding: 15px;
          background: #2c1f14;
          color: #f0e4cf;
          border: none;
          border-radius: 8px;
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          cursor: pointer;
          margin-top: 8px;
          position: relative;
          overflow: hidden;
          transition: background 0.25s, transform 0.15s;
          animation: fadeUp 0.6s 0.75s both;
        }

        .login-btn::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(200,160,90,.15) 0%, transparent 60%);
          opacity: 0;
          transition: opacity 0.3s;
        }

        .login-btn:hover:not(:disabled) { background: #3d2a1a; transform: translateY(-1px); }
        .login-btn:hover:not(:disabled)::after { opacity: 1; }
        .login-btn:active:not(:disabled) { transform: translateY(0); }
        .login-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .btn-inner {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .spinner {
          width: 14px;
          height: 14px;
          border: 1.5px solid rgba(240,228,207,.3);
          border-top-color: #f0e4cf;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }

        .form-footer {
          margin-top: 32px;
          padding-top: 24px;
          border-top: 1px solid #e8d8c8;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          animation: fadeUp 0.6s 0.85s both;
        }

        /* ── GUEST CTA  ──────────────────────────────────────────────
           Sits below the form and points visitors who landed on the
           staff login by mistake to the public reservation flow.  Same
           gold-on-dark treatment as the navbar's primary CTA so it
           reads as the marketing path, not part of the staff form. */
        .guest-cta {
          margin-top: 22px;
          padding: 22px 24px;
          background:
            linear-gradient(135deg, rgba(200,160,90,.10) 0%, rgba(200,160,90,.02) 100%),
            #fdf8f2;
          border: 1px solid rgba(200,160,90,.28);
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 18px;
          animation: fadeUp 0.6s 0.95s both;
        }

        .guest-cta-icon {
          flex-shrink: 0;
          width: 44px; height: 44px;
          border-radius: 50%;
          background: rgba(200,160,90,.12);
          border: 1px solid rgba(200,160,90,.35);
          display: flex; align-items: center; justify-content: center;
          color: #c8a05a;
        }

        .guest-cta-text {
          flex: 1;
          line-height: 1.35;
        }
        .guest-cta-eyebrow {
          font-size: 9.5px;
          font-weight: 500;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #c8a05a;
          margin-bottom: 3px;
        }
        .guest-cta-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 18px;
          font-weight: 400;
          color: #2c1f14;
        }
        .guest-cta-title em { font-style: italic; color: #c8a05a; }

        .guest-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 11px 20px;
          background: #c8a05a;
          color: #1a110a;
          border: 1.5px solid #c8a05a;
          border-radius: 6px;
          font-family: 'Jost', sans-serif;
          font-size: 10.5px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          text-decoration: none;
          flex-shrink: 0;
          transition: background 0.25s, transform 0.15s, box-shadow 0.25s;
        }
        .guest-cta-btn:hover {
          background: #d4ae6a;
          transform: translateY(-1px);
          box-shadow: 0 6px 18px rgba(200,160,90,.28);
        }

        @media (max-width: 480px) {
          .guest-cta { flex-direction: column; align-items: flex-start; gap: 14px; padding: 18px 18px; }
          .guest-cta-btn { width: 100%; justify-content: center; }
        }

        .footer-dot { width: 3px; height: 3px; border-radius: 50%; background: #c0aea0; }

        .footer-text {
          font-size: 11.5px;
          font-weight: 300;
          color: #a09080;
          letter-spacing: 0.06em;
        }

        /* ── ANIMATIONS ── */
        @keyframes panelIn {
          from { opacity: 0; transform: translateX(-30px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(30px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes photoDrift {
          from { transform: scale(1.05) translate3d(0, 0, 0); }
          to   { transform: scale(1.12) translate3d(-1.5%, -1%, 0); }
        }
        @keyframes shake {
          10%, 90% { transform: translateX(-2px); }
          20%, 80% { transform: translateX(3px); }
          30%, 50%, 70% { transform: translateX(-4px); }
          40%, 60% { transform: translateX(4px); }
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 720px) {
          .login-left { display: none; }
          .login-right { width: 100%; padding: 48px 28px; }
          .login-right::before { display: none; }
        }
      `}</style>

      <div className="login-root">
        {/* LEFT BRANDING PANEL */}
        <div className="login-left">
          {/* Dim hotel backdrop sits behind every decorative layer. */}
          <div className="left-photo" />
          <div className="left-texture" />

          <div className="left-ornament">
            {facility?.logo ? (
              <img
                src={`${ASSETS}${facility.logo}`}
                alt="Logo"
                style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }}
              />
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#c8a05a" strokeWidth="1.2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            )}
          </div>

          <div style={{ position: "relative", zIndex: 3 }}>
            <div className="left-divider" />
            <p className="left-tagline">Management Portal</p>
            <h1 className="left-title">
              {firstWord || "Hotel"}
              {restName ? <><br /><em>{restName}</em></> : null}
            </h1>
            <p className="left-desc">
              Secure access to your hospitality management dashboard. Manage reservations, guests, and operations with ease.
            </p>
          </div>

          <div className="left-footer">
            <div className="dot" />
            <div className="dot active" />
            <div className="dot" />
          </div>
        </div>

        {/* RIGHT FORM PANEL */}
        <div className="login-right">
          <p className="form-eyebrow">Staff Access</p>
          <h2 className="form-title">Welcome back</h2>
          <p className="form-subtitle">Sign in to continue to the management area.</p>

          {notice && !error && (
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "11px 14px", borderRadius: 8,
              background: "rgba(200,160,90,.12)",
              border: "1px solid rgba(200,160,90,.45)",
              color: "#6b4f1e", fontSize: 13, fontWeight: 400,
              marginBottom: 18,
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              {notice}
            </div>
          )}

          {error && (
            <div className="error-box">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div className="field-group">
              <label className="field-label" htmlFor="username">Username</label>
              <div className="field-wrap">
                <svg className="field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
                <input
                  id="username"
                  className="field-input"
                  type="text"
                  name="username"
                  placeholder="your username"
                  value={form.username}
                  onChange={handleChange}
                  autoComplete="username"
                  required
                />
              </div>
            </div>

            <div className="field-group">
              <label className="field-label" htmlFor="password">Password</label>
              <div className="field-wrap">
                <svg className="field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <input
                  id="password"
                  className="field-input"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button type="submit" className="login-btn" disabled={loading}>
              <span className="btn-inner">
                {loading && <span className="spinner" />}
                {loading ? "Signing in…" : "Sign In"}
              </span>
            </button>
          </form>

          <div className="form-footer">
            <span className="footer-text">{facility?.name || "Hotel"}</span>
            <div className="footer-dot" />
            <span className="footer-text">Management System</span>
          </div>

          {/* Visitor-side fallback — this is the staff portal, but anyone
              who arrived expecting to book a room should be one click
              away from the public reservation flow. */}
          <div className="guest-cta">
            <div className="guest-cta-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2h-4v-7H10v7H5a2 2 0 0 1-2-2z"/>
              </svg>
            </div>
            <div className="guest-cta-text">
              <div className="guest-cta-eyebrow">Just visiting?</div>
              <div className="guest-cta-title">
                Book a stay at <em>{facility?.name || "our hotel"}</em>
              </div>
            </div>
            <Link to="/reserve" className="guest-cta-btn">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 7h18v13H3z"/><path d="M3 12h18"/>
              </svg>
              Reserve a Room
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}